import { useState } from "react";
import toast from "react-hot-toast";
import { format } from "date-fns";
import { Download, Mail, Pencil, Plus } from "lucide-react";
import { SendEmailModal } from "pages/patient/PatientDocumentsPage/components/SendEmailModal/SendEmailModal";

import { useDownloadLetter } from "components/AddLetterForm/hooks/useDownloadLetter";
import { Button } from "components/ui/Button";
import { DataTable } from "components/ui/DataTable";
import { useDataTableSelection } from "components/ui/DataTable/hooks/useDataTableSelection";
import { Pill, PILL_COLORS } from "components/ui/Pill";
import { PatientNameLink } from "features/communications/components/PatientNameLink/PatientNameLink";
import { usePracticeLetters } from "features/communications/hooks/usePracticeLetters";
import { SelectPatientDialog } from "features/patients/components/SelectPatientDialog";
import { usePatientLetters } from "hooks/usePatientLetters";
import { toRows, toTotalRecords } from "utils/paginatedResponse";

import { AddLetterModal } from "./AddLetterModal";
import { EditLetterModal } from "./EditLetterModal";
import styles from "./LettersSection.module.css";

const ROWS_OPTIONS = [10, 20, 50];

const formatDate = (value) => {
  if (!value) return "—";

  try {
    return format(new Date(value), "dd MMM yyyy");
  } catch {
    return "—";
  }
};

const getAuthorName = (row) => {
  if (row.staffMember) {
    const firstName = row.staffMember.firstName ?? "";
    const lastName = row.staffMember.lastName ?? "";
    const name = `${firstName} ${lastName}`.trim();

    return name || row.staffId || "—";
  }

  return row.staffId || "—";
};

export const LettersSection = ({ patientId, patient }) => {
  const tenantVal = localStorage.getItem("kinesin-tenant") ?? "";
  const { mutate: downloadLetter } = useDownloadLetter();

  /*
   * With no patient in context this is the practice-wide list on the global
   * Communications page: every letter in the tenant, with a Patient column, and without
   * the actions that need a patient to act on (attaching letters from several patients
   * to one email). Adding a letter asks which patient it is for first.
   */
  const isPracticeWide = !patientId;

  const [selectedLetter, setSelectedLetter] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSelectPatient, setShowSelectPatient] = useState(false);
  /*
   * Practice-wide there is no patient in context, so "Add Letter" asks for one first and
   * the picked patient — not the (absent) prop — is what the form writes to.
   */
  const [letterPatient, setLetterPatient] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailDocuments, setEmailDocuments] = useState([]);
  const [emailPatient, setEmailPatient] = useState(null);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  /*
   * The table's own selection, rather than a Set kept here and a checkbox column of our
   * own. That gives the tick its usual place in the first column, a select-all in the
   * header and the shared bulk-action bar — the three things this list was reimplementing.
   *
   * Keyed by documentId because that is what an email attaches; a letter whose PDF has not
   * been generated has none, which is exactly the row that must not be selectable.
   */
  const selection = useDataTableSelection((row) => row.documentId);

  const patientLettersQuery = usePatientLetters(patientId, {
    page,
    rows: rowsPerPage,
  });
  const practiceLettersQuery = usePracticeLetters({ enabled: isPracticeWide });

  const { data, isLoading, isError, refetch } = isPracticeWide
    ? practiceLettersQuery
    : patientLettersQuery;

  // The tenant-wide endpoint answers with a plain list; the per-patient one paginates.
  const letters = toRows(data);
  const totalCount = toTotalRecords(data);

  const letterPatientId = patientId ?? letterPatient?.id;

  const handleAddLetterClick = () => {
    if (isPracticeWide) {
      setShowSelectPatient(true);

      return;
    }

    setShowAddModal(true);
  };

  const handlePatientPicked = (pickedPatient) => {
    setLetterPatient(pickedPatient);
    setShowSelectPatient(false);
    setShowAddModal(true);
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setLetterPatient(null);
  };

  const handleOpenSendEmail = (letter) => {
    if (!letter?.documentId) {
      toast.error("PDF isn't ready yet — please wait before sending.");
      return;
    }

    setEmailDocuments([{
      id: letter.documentId,
      name: letter.name || "Letter",
      documentType: "Letter",
    }]);
    setEmailPatient(letter.patient ?? patient);
    setShowEmailModal(true);
  };

  const columns = [
    {
      id: "documentCreated",
      header: "Date",
      width: "110px",
      render: (row) => formatDate(row.documentCreated),
    },
    isPracticeWide && {
      id: "patient",
      header: "Patient",
      width: "120px",
      flexGrow: 1,
      render: (row) => (
        <PatientNameLink patientId={row.patientId} patient={row.patient} />
      ),
    },
    {
      id: "name",
      header: "Name",
      width: "140px",
      flexGrow: 1,
      render: (row) => row.name || "—",
    },
    {
      id: "letterType",
      header: "Type",
      width: "110px",
      // A pill, like every other categorical column in the app — plain text here read
      // as data rather than as a classification.
      render: (row) =>
        row.letterType ? (
          <Pill {...PILL_COLORS.reactionUnknown}>{row.letterType}</Pill>
        ) : (
          "—"
        ),
    },
    {
      id: "author",
      header: "Author",
      width: "120px",
      render: (row) => getAuthorName(row),
    },
  ].filter(Boolean);

  /*
   * The shared bulk bar carries the count and a Clear of its own, so this supplies only
   * the action. A letter with no documentId has no PDF to attach and is filtered out
   * rather than silently attaching nothing.
   */
  const bulkActions = [
    {
      label: "Attach to Email",
      icon: Mail,
      onClick: (selectedLetters) => {
        const documents = (selectedLetters ?? [])
          .filter((letter) => letter.documentId)
          .map((letter) => ({
            id: letter.documentId,
            name: letter.name || "Letter",
            documentType: "Letter",
          }));

        if (!documents.length) {
          toast.error("PDF isn't ready yet — please wait before sending.");

          return;
        }

        setEmailDocuments(documents);
        setEmailPatient(patient);
        setShowEmailModal(true);
      },
    },
  ];

  const rowActions = [
    {
      label: "Edit",
      icon: Pencil,
      primary: true,
      onClick: (row) => setSelectedLetter(row),
    },
    {
      label: "Download PDF",
      icon: Download,
      onClick: (row) => {
        if (!row.documentId) {
          toast.error("PDF isn't ready yet.");
          return;
        }
        // The endpoint looks the letter up by its own id (not documentId) —
        // it just uses documentId internally to fetch the stored file.
        downloadLetter({
          id: row.id,
          tenantVal,
          patientId: patientId ?? row.patientId,
        });
      },
    },
    {
      label: "Send Email",
      icon: Mail,
      onClick: handleOpenSendEmail,
    },
  ];

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Letters</h2>

        <Button variant="text" endIcon={Plus} onClick={handleAddLetterClick}>
          Add Letter
        </Button>
      </div>

      {isError && (
        <div className={styles.errorBanner}>Failed to load letters.</div>
      )}

      <DataTable
        columns={columns}
        data={letters}
        getRowId={(row) => row.id}
        isLoading={isLoading}
        rowActions={rowActions}
        onRowClick={(row) => setSelectedLetter(row)}
        totalCount={totalCount}
        currentPage={page}
        onPageChange={setPage}
        rowsValue={rowsPerPage}
        onRowsChange={(value) => {
          setRowsPerPage(value);
          setPage(1);
        }}
        rowsOptions={ROWS_OPTIONS}
        selectable={!isPracticeWide}
        selection={selection}
        bulkActions={bulkActions}
        emptyTitle="No letters found"
        emptyText={
          isPracticeWide
            ? "No letters have been added in this practice."
            : "No letters have been added for this patient."
        }
      />

      <EditLetterModal
        open={!!selectedLetter}
        letterId={selectedLetter?.id ?? null}
        patientId={patientId ?? selectedLetter?.patientId}
        onClose={() => setSelectedLetter(null)}
        refetchList={refetch}
      />

      <SelectPatientDialog
        open={showSelectPatient}
        onClose={() => setShowSelectPatient(false)}
        onSelect={handlePatientPicked}
        title="Add Letter"
        description="Choose the patient this letter belongs to."
      />

      <AddLetterModal
        open={showAddModal}
        patientId={letterPatientId}
        patient={patient ?? letterPatient}
        onClose={handleCloseAddModal}
        onSuccess={() => {
          handleCloseAddModal();
          refetch();
        }}
      />

      <SendEmailModal
        isOpen={showEmailModal}
        onClose={() => {
          setShowEmailModal(false);
          setEmailDocuments([]);
          setEmailPatient(null);
        }}
        patient={emailPatient ?? patient}
        documents={emailDocuments}
      />
    </div>
  );
};
